import React, { useState, useEffect } from 'react';
import { BRAND_NAME } from '../../theme/brand';
import {
    Box,
    Card,
    CardBody,
    Heading,
    Text,
    VStack,
    HStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Tag,
    TableContainer,
    Button
} from '@chakra-ui/react';
import {

    FiCopy,
    FiCheck
} from 'react-icons/fi';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-json';
import { fetchWrapper } from '../../helpers';

export const CustomEventAPI = () => {
    const [eventKey, setEventKey] = useState('');
    const [copiedSection, setCopiedSection] = useState(null);

    const copyToClipboard = (text, section) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedSection(section);
            setTimeout(() => setCopiedSection(null), 2000);
        });
    };

    const loadEventKey = async () => {
        const url = `${process.env.REACT_APP_API_URL}/customer/get-key?key_type=event_key`;
        const data = await fetchWrapper.get(url)

        setEventKey(data?.key || '')
    }

    useEffect(() => {
        loadEventKey()
    }, [])


    const requestExample = `{
  "contact_no": "923xxxxxxxx14",
  "event_name": "cash_withdrawal",
  "properties": {
    "amount": "20000",
    "atm_location": "DHA Phase 8, Karachi",
    "timestamp": "2025-11-12 10:45:30"
  }
}`;

    const responseExample = `{
  "status": "accepted",
  "statusCode": 200,
  "message": "Event inserted successfully",
  "accepttime": 1727247788
}`;

    const responseCodes = [
        { code: "200", status: "Accepted", description: "Event has been inserted Successfully", color: "green" },
        { code: "401", status: "Authentication Failed", description: "API Key Missing, Invalid API Key or API Key does not Exist in our Database", color: "red" },
        { code: "403", status: "Account Inactive", description: "Customer Account Mark as Inactive in The System", color: "red" },
        { code: "403", status: "Validation Failed", description: "Validation Failed or Data Missing", color: "red" },
        { code: "404", status: "Failed", description: "Event Insert Failed", color: "red" },
        { code: "500", status: "Server Error", description: "Internal server error - please retry", color: "red" },
        { code: "503", status: "Service Unavailable", description: "Service temporarily unavailable - maintenance or high load", color: "orange" }
    ];

    return (
        <VStack spacing={8} align="stretch" mt={4}>
            {/* Important Note */}
            <Card shadow="sm" bg="#fefce8" border="1px solid" borderColor="#fff085">
                <CardBody p={4}>
                    <HStack align="flex-start" spacing={2}>
                        <Text fontWeight="bold" color="#733e0a">Important:</Text>
                        <Text color="#733e0a">Each API call should contain only one event.</Text>
                    </HStack>
                </CardBody>
            </Card>

            {/* API Endpoint */}
            <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg" p={4}>
                <HStack mb={3} flexWrap="wrap" gap={2}>
                    <Tag borderRadius="md" fontSize="12px" fontWeight="600" bg="#ff6900" color="white">
                        POST
                    </Tag>
                    <Tag bg="#fefce8" color="#a65f00" border="1px solid #fff085" px={4}>
                        {process.env.REACT_APP_EVENT_API_URL || 'https://api.xnotify.ai'}/v2/save
                    </Tag>
                </HStack>
                <Text color="gray.700">
                    The {BRAND_NAME} Event Ingestion API enables clients to send any type of event—without relying on a predefined schema—by defining their own event names, structures, and data fields.
                </Text>
            </Box>

            {/* Headers Section */}
            <Box>
                <Heading size="md" mb={4}>Headers</Heading>
                <TableContainer bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
                    <Table variant="simple">
                        <Thead bg="#f7f7f7">
                            <Tr>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Key</Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Value</Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Description</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <Tr>
                                <Td color="black" fontWeight="500">x-event-key</Td>
                                <Td whiteSpace="normal" wordBreak="break-word">
                                    {eventKey}
                                    <Text fontSize="xs" color="gray.500" mt={1}>This is your Event API Key</Text>
                                </Td>
                                <Td whiteSpace="normal" wordBreak="break-word">Your unique API key for authentication.</Td>
                            </Tr>
                            <Tr>
                                <Td color="black" fontWeight="500">Content-Type</Td>
                                <Td whiteSpace="normal" wordBreak="break-word">application/json</Td>
                                <Td whiteSpace="normal" wordBreak="break-word">Specifies the format of the payload</Td>
                            </Tr>
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Request Payload Section */}
            <Box>
                <Heading size="md" mb={2}>Request Payload</Heading>
                <Card p={3} shadow="sm" border="1px solid" borderColor="gray.200">
                    <Text color="gray.600" mb={4} whiteSpace="normal">
                        The request payload must be sent in JSON format, containing the recipient's number, event name and properties.
                    </Text>
                    <Card bg="#1E1E1E" color="white" position="relative">
                        <Button
                            position="absolute"
                            top={3}
                            right={3}
                            size="sm"
                            colorScheme="whiteAlpha"
                            leftIcon={copiedSection === 'request' ? <FiCheck /> : <FiCopy />}
                            onClick={() => copyToClipboard(requestExample, 'request')}
                        >
                            {copiedSection === 'request' ? 'Copied!' : 'Copy'}
                        </Button>
                        <CardBody>
                            <Box as="pre" whiteSpace="pre-wrap" wordBreak="break-word">
                                <code className="language-json">{requestExample}</code>
                            </Box>
                        </CardBody>
                    </Card>
                </Card>
            </Box>

            {/* Payload Fields Section */}
            <Box>
                <Heading size="md" mb={4}>Payload Fields</Heading>
                <TableContainer bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
                    <Table variant="simple">
                        <Thead bg="#f7f7f7">
                            <Tr>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Field</Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Type</Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Required</Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Description</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <Tr>
                                <Td whiteSpace="normal" w="150px">contact_no</Td>
                                <Td whiteSpace="normal">String</Td>
                                <Td whiteSpace="normal"><Tag bg="#dc2626" color="white" fontSize="xs">Yes</Tag></Td>
                                <Td whiteSpace="normal" wordBreak="break-word">The customer phone number used to identify the customer</Td>
                            </Tr>
                            <Tr>
                                <Td whiteSpace="normal" wordBreak="break-word">event_name</Td>
                                <Td whiteSpace="normal">String</Td>
                                <Td whiteSpace="normal"><Tag bg="#dc2626" color="white" fontSize="xs">Yes</Tag></Td>
                                <Td whiteSpace="normal" wordBreak="break-word">The Event Name serves as the unique identifier for all event type. Must remain unchanged and written without spaces.</Td>
                            </Tr>
                            <Tr>
                                <Td whiteSpace="normal" wordBreak="break-word">properties</Td>
                                <Td whiteSpace="normal">Object</Td>
                                <Td whiteSpace="normal"><Tag bg="#dc2626" color="white" fontSize="xs">Yes</Tag></Td>
                                <Td whiteSpace="normal" wordBreak="break-word">The properties field captures the specific attributes or context of an event. You can add as many variables as needed.</Td>
                            </Tr>
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Response Section */}
            <Box>
                <Heading size="md" mb={2}>Response</Heading>
                <Card p={3} shadow="sm" border="1px solid" borderColor="gray.200">
                    <Text color="gray.600" mb={4} whiteSpace="normal">
                        If the event is successfully inserted, you will receive a response like this:
                    </Text>
                    <Card bg="#1E1E1E" color="white" position="relative">
                        <Button
                            position="absolute"
                            top={3}
                            right={3}
                            size="sm"
                            colorScheme="whiteAlpha"
                            leftIcon={copiedSection === 'response' ? <FiCheck /> : <FiCopy />}
                            onClick={() => copyToClipboard(responseExample, 'response')}
                        >
                            {copiedSection === 'response' ? 'Copied!' : 'Copy'}
                        </Button>
                        <CardBody>
                            <Box as="pre" whiteSpace="pre-wrap" wordBreak="break-word">
                                <code className="language-json">{responseExample}</code>
                            </Box>
                        </CardBody>
                    </Card>
                </Card>
            </Box>

            {/* API Response Codes Section */}
            <Box>
                <Heading size="md" mb={4}>API Response Code</Heading>
                <TableContainer bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
                    <Table variant="simple">
                        <Thead bg="#f7f7f7">
                            <Tr>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Code</Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Status</Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Description</Th>
                            </Tr>
                        </Thead>
                        <Tbody fontSize="14px">
                            {responseCodes.map((item, index) => (
                                <Tr key={index} _hover={{ bg: "gray.50" }}>
                                    <Td color="black" fontWeight="500" py={4}>{item.code}</Td>
                                    <Td py={4}>
                                        <Tag
                                            bg={item.color === 'green' ? '#22c55e' : item.color === 'red' ? '#dc2626' : '#f97316'}
                                            color="white"
                                            fontSize="xs"
                                        >
                                            {item.status}
                                        </Tag>
                                    </Td>
                                    <Td color="black" py={4} whiteSpace="normal" wordBreak="break-word">{item.description}</Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Other Information Section */}
            <Box>
                <Heading size="md" mb={4}>Other Information</Heading>
                <TableContainer bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
                    <Table variant="simple">
                        <Thead bg="#f7f7f7">
                            <Tr>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Key</Th>
                                <Th fontSize="14px" fontWeight="600" color="black" textTransform="none">Description</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <Tr>
                                <Td whiteSpace="normal" wordBreak="break-word">accepttime</Td>
                                <Td whiteSpace="normal" wordBreak="break-word">This time will received and accepted the message request</Td>
                            </Tr>
                        </Tbody>
                    </Table>
                </TableContainer>
            </Box>
        </VStack>
    );
};